import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RelatedReading — bidirectional internal-linking widget.
 *
 * Why this exists:
 *   External SEO audits (thestacc) flagged that pillar/product/city pages
 *   never linked OUT to relevant blogs — only one-way (blog → pillar).
 *   Topic-cluster SEO requires both directions.
 *
 * Usage:
 *   <RelatedReading
 *     keywords={["workstation", "Mumbai", "BFSI"]}
 *     heading="Related Insights from Our Knowledge Library"
 *     limit={3}
 *     testId="related-reading-product-detail"
 *   />
 *
 * Relevance ranking:
 *   - Score each blog by counting keyword matches in title (×3) + excerpt (×1).
 *   - Fall back to most-recent blogs if nothing scores > 0.
 */
export default function RelatedReading({
  keywords = [],
  heading = "Related Reading",
  subheading = "More from our knowledge library on this topic.",
  limit = 3,
  testId = "related-reading",
}) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        const all = Array.isArray(response.data) ? response.data : [];
        const lowerKeywords = keywords.map((k) => k.toLowerCase()).filter(Boolean);

        const scored = all.map((b) => {
          const title = (b.title || "").toLowerCase();
          const excerpt = (b.excerpt || "").toLowerCase();
          let score = 0;
          for (const kw of lowerKeywords) {
            if (title.includes(kw)) score += 3;
            if (excerpt.includes(kw)) score += 1;
          }
          return { ...b, _score: score };
        });

        const matched = scored.filter((b) => b._score > 0).sort((a, b) => b._score - a._score);
        const filler = scored.filter((b) => b._score === 0); // already date-sorted by API
        const picked = [...matched, ...filler].slice(0, limit);

        if (!cancelled) setBlogs(picked);
      } catch (e) {
        if (!cancelled) setBlogs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBlogs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords.join("|"), limit]);

  if (loading || blogs.length === 0) return null;

  return (
    <section className="py-16 bg-[#F8FAFC]" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <BookOpen className="w-6 h-6 text-[#F97316]" />
          <div>
            <span className="font-mono text-xs text-[#F97316] tracking-wider uppercase block">
              Knowledge Library
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">{heading}</h2>
            {subheading && <p className="text-sm text-[#64748B] mt-1">{subheading}</p>}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog, idx) => (
            <motion.article
              key={blog.id || blog.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              className="group bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors"
              data-testid={`${testId}-card-${idx}`}
            >
              <Link to={`/blog/${blog.slug}`} className="block p-6">
                <span className="font-mono text-[10px] text-[#F97316] tracking-wider uppercase mb-3 block">
                  {blog.category || "Insights"}
                </span>
                <h3 className="text-base font-bold text-[#0F172A] mb-2 line-clamp-2 group-hover:text-[#F97316] transition-colors">
                  {blog.title}
                </h3>
                <p className="text-xs text-[#64748B] line-clamp-3 mb-4">{blog.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F97316] group-hover:gap-2 transition-all">
                  Read article
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
